import { PageProps, KiNoPost, OrderPicklistExtra } from '../../components/types'
import HeaderDetail from '../../components/HeaderDetail'
import { 
  BasePicklistItemColumn,
  PicklistItemWithDrawColumn,
  PicklistItemCheckMvColumn
} from '../../constants/Column'
import { syncPicklistOrder } from '../../apis'
import Modal from '../../components/Modal'
import { groupBy as rowGrouper } from 'lodash';

import OrderPicklistReducer, { 
  INITIAL_STATE, 
  PicklistAction
} from '../../states/PicklistReducer'
import {
  whApprovePicklistExtra,
  getMovementsById,
  getExtraPicklist,
  cancelExtraReturnPicklist,
  isWhWithdrawPicklist
} from '../../states/PicklistAction'

import DataGrid from 'react-data-grid'
import { useReducer, useEffect, useState, useMemo } from 'react'

import { 
  AiOutlineCloudSync, 
  AiOutlineCheckCircle, 
  AiOutlineQuestionCircle 
} from 'react-icons/ai'

import { 
  ImCancelCircle
} from 'react-icons/im'

import { DgtPdOrderPicklist, ExtraReturnStatus } from '../../models'
import { useHistory, Link } from 'react-router-dom'

import { useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { formatDateFromUnix } from '../../utilities/Format'

const Loading = () => <div className="w-4 h-4 border-2 border-white border-solid rounded-full animate-spin border-t-transparent" />

const GoodIssueDetailExtra = ({ title, id, username, extraId, readOnly, orderType } : PageProps) => {
  const [ state, dispatch ] = useReducer(
    OrderPicklistReducer, INITIAL_STATE);
  
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  );
  const [ showDialogCancel, setShowCancel ] = useState<boolean>(false)
  const [ loading, setLoading ] = useState<boolean>(false)
  const [ invalidPicklist, setInvalidPicklist ] = useState<boolean>(false)
  const [ originalExtra, setOriginalExtra ] = useState<OrderPicklistExtra>()

  const history = useHistory()

  const formMethods = useForm<KiNoPost>({
    defaultValues: {
      kiNo: ''
    },
    resolver: yupResolver(yup.object({
      kiNo: yup.string(),
    }))
  })
  
  useEffect(() => {
    const fetchExtra = async (id: string) => {
      const extraPicklist = await getExtraPicklist(id)
      if (extraPicklist) {
        const orderInfo : DgtPdOrderPicklist = JSON.parse(extraPicklist?.orderInfo)
        
        const extraItems = extraPicklist.extraReturnItems || []
        const toPicklistItems: any = extraItems.map(itm => ({
          pk: itm?.orderId || extraPicklist.orderId,
          sk: `ITEM#${itm?.materialCode}`,
          id: itm?.id,
          materialType: '',
          materialCode: itm?.materialCode,
          materialDescription: itm?.materialDescription,
          materialUnit: itm?.materialUnit,
          batchLotNo: itm?.batchLotNo,
          requestQty: itm?.qty
        }))

        dispatch({ type: PicklistAction.Inited, payload: { header: {
          ...orderInfo,
          requestedBy: extraPicklist?.approvedBy, 
          requestedDateTime: extraPicklist?.approvedDateTime,
          approvedBy: extraPicklist.withdrawedBy ? extraPicklist.withdrawedBy : undefined,
          approvedDateTime: extraPicklist.withdrawedDateTime ? extraPicklist.withdrawedDateTime : undefined,
          kiNo: extraPicklist?.kiNo ? extraPicklist.kiNo : undefined,
          commentText: `${extraPicklist?.remark || ''}${extraPicklist.canceledDateTime ? `(ยกเลิกเมื่อ ${formatDateFromUnix(extraPicklist.canceledDateTime)})` : ''}`,
          extraReturnId: extraPicklist?.id,
          status: extraPicklist.status === 'CANCELED' ? extraPicklist.status : orderInfo.status
        }, items: toPicklistItems, comments: []}})
        formMethods.setValue('kiNo', extraPicklist.kiNo || '')

        setOriginalExtra(extraPicklist)
      }
    }

    fetchExtra(extraId!)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ extraId ])

  const openDialog = () => {
    dispatch({ type: PicklistAction.OpenDialog })
  }

  const { header, items, isSyncing, 
    errorMessage, showConfirmDialog, itemMovements, isSubmitting } = state

  useEffect(() => {
    const fetchMovements = async (orderId: string) => {
      const mvs = await getMovementsById(orderId)
      // not approved
      // if approved
      const onlyExtraMovements = mvs.filter(
        mv => mv.movementType !== '262'
        && (!mv.approvedDateTime || mv.extraRefId === extraId)
        && items.map(i => i.materialCode).includes(mv.materialCode)
        && mv.materialDescription !== 'DIW'
      ).map(mv => ({
        ...mv, 
        materialRequestQty: items.find(item => item.materialCode === mv.materialCode)?.requestQty
      }))
      dispatch({ 
        type: PicklistAction.PicklistItemMovementsUpdated, 
        payload: { 
          itemMovements: onlyExtraMovements
        }})
    }
    if (items.length > 0) fetchMovements(id!)
  }, [ id, extraId, items ])

  const clickSyncSap = async () => {
    dispatch({ type: PicklistAction.StartSync })
    // sync from sap
    const result = await syncPicklistOrder(
      state.header?.pk!,
      state.header?.division!, 
      state.items.map(itm => {
        return {
          materialCode: itm.materialCode,
          materialDescription: itm.materialDescription,
          materialType: itm.materialType,
          materialRequestQty: itm.requestQty
        }
      }),
      state.itemMovements.map(mv => mv.sk),
      true
    );
    const mvs = await getMovementsById(state.header?.pk!)
    const onlyExtraMovements = mvs.filter(
        mv => mv.movementType !== '262'
        && (!mv.approvedDateTime || mv.extraRefId === extraId)
        && items.map(i => i.materialCode).includes(mv.materialCode)
        && mv.materialDescription !== 'DIW'
      ).map(mv => ({
        ...mv, 
        materialRequestQty: items.find(item => item.materialCode === mv.materialCode)?.requestQty
      }))
    dispatch({
      type: PicklistAction.SyncCompleted, 
      payload: { 
        errorSync : result.success && result.message ? '' : result.message,
        itemMovements: onlyExtraMovements
      }
    })
  }

  const onApproved = async () => {
    const kiNo = formMethods.getValues('kiNo')
    dispatch({ type: PicklistAction.PicklistUpdating })
    const updated = await whApprovePicklistExtra(extraId!, originalExtra!, itemMovements, state.items, username!, kiNo)
    const head : DgtPdOrderPicklist = {
      ...header!,
      approvedBy: updated.withdrawedBy,
      approvedDateTime: updated.withdrawedDateTime,
      kiNo: updated.kiNo ? updated.kiNo : undefined
    }
    dispatch({ type: PicklistAction.PicklistUpdated, payload: { header: head } })
    history.goBack()
  }
  
  const closeDialog = () => {
    dispatch({ type: PicklistAction.CloseDialog })
  }

  const isBlockApprove = itemMovements.length === 0

  useEffect(() => {
    if (itemMovements.length > 0) {
      setExpandedGroupIds(new Set(itemMovements.map(itm => itm.materialCode)))
    }
  }, [ itemMovements ])
  
  const withDrawItems = new Set(itemMovements.map(itm => itm.materialCode))
  
  const onSubmit: SubmitHandler<KiNoPost> = async (data) => {
    setLoading(true)

    const isMainPicklistWithdrawed = await isWhWithdrawPicklist(id!)
    if (isMainPicklistWithdrawed) {
      setInvalidPicklist(false)
      openDialog()
    } else {
      setInvalidPicklist(true)
    }

    setLoading(false)
  }

  const onCancel = async () => {
    await cancelExtraReturnPicklist(originalExtra!, username!)
    history.goBack()
  }
  
  const isCanceled = (header && header.status === ExtraReturnStatus.CANCELED)
  
  const mapBarcodeMovements = useMemo(() => {
    return itemMovements.map(mv => {
      const barcodeItem = items.find(itm => itm.materialCode === mv.materialCode)?.barcodePkg
      if (barcodeItem) return { ...mv, barcode: barcodeItem }
      return mv
    })
  }
  , [ itemMovements, items ])

  return <div className="page-container">
    <FormProvider {...formMethods} >
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
      <HeaderDetail title={`${title} Order : ${id} / เบิกเพิ่ม ${header?.kiNo ? `(Ref No. ${header?.kiNo})` : `(ขอเมื่อ ${formatDateFromUnix(header?.requestedDateTime)})`}`} header={header}
        isShowRelease={false} isInputKi={true} isShowRemark={true}
        readOnly={readOnly || isCanceled}/>
      {readOnly || isCanceled ? null : <div className='flex items-end justify-between mt-5 mb-2 md:mt-1'>
        <div className='flex items-center gap-2'>
          <button type='button' className='gap-2 btn-sap' onClick={clickSyncSap} disabled={isSyncing}>
            {isSyncing ? <><Loading /> กำลังเชื่อมต่อ SAP</> : <><AiOutlineCloudSync size={24} /> กดเพื่อดึงยอดจ่ายจาก SAP</>}
          </button>
          <small className='text-red-500 '>{errorMessage}</small>
        </div>
        <div className='flex items-center gap-2'>
          <button className='gap-1 btn-outline-danger'
            onClick={() => setShowCancel(true)}
            type='button'>
              <ImCancelCircle />ยกเลิก
          </button>
          <button className='gap-1 btn-success'
            disabled={isBlockApprove || loading}
          >
              <AiOutlineCheckCircle />กดเพื่ออนุมัติ
          </button>
        </div>
      </div>}
      {invalidPicklist ? <small className='float-right italic text-red-500'>ใบเบิก ORDER {id} ยังไม่ได้รับการอนุมัติจ่าย กดลิ้งค์ด้านล่าง เพื่ออนุมัติจ่ายใบเบิกหลัก</small> : null}
      </form>
    </FormProvider>
    <small>จ่าย {withDrawItems.size} จากทั้งหมด {items.length} รายการ</small>
    {mapBarcodeMovements.length === 0 ?
      <DataGrid 
        className='mb-3 '
        style={{ height: "100%", contain: "none" }}
        columns={[...BasePicklistItemColumn, ...PicklistItemWithDrawColumn]}
        rows={items}
        defaultColumnOptions={{ resizable: true }}
        rowHeight={(arg) => {
          const itm = arg.row as DgtPdOrderPicklist
          if (itm.barcodePkg) return 55
          return 35
        }}
      />
      :
      <>
        <DataGrid
          className='mb-3 '
          style={{ height: "100%", contain: "none" }}
          columns={PicklistItemCheckMvColumn.slice(0, -2)} // remove SelectColumn/CheckTime
          rows={mapBarcodeMovements}
          rowKeyGetter={(row) => row.id}
          groupBy={['materialCode']}
          rowGrouper={rowGrouper}
          expandedGroupIds={expandedGroupIds}
          onExpandedGroupIdsChange={setExpandedGroupIds}
          defaultColumnOptions={{ resizable: true }}
          rowHeight={(arg) => {
            if (arg.type === 'GROUP' && arg.row.childRows[0].barcode) return 55
            return 35
          }}
        />
        <small className={`${items.length - withDrawItems.size === 0 ? 'hidden':''}`}>
          รายการที่ไม่มีจ่าย
        </small>
        <DataGrid 
          className={` mb-3 ${items.length - withDrawItems.size === 0 ? 'hidden':''}`}
          style={{ height: "100%", contain: "none" }}
          columns={[...BasePicklistItemColumn, ...PicklistItemWithDrawColumn].slice(0, -1)}
          rows={items.filter(
            itm => !Array.from(withDrawItems).includes(itm.materialCode)
          )}
          defaultColumnOptions={{ resizable: true }}
          rowHeight={(arg) => {
            const itm = arg.row as DgtPdOrderPicklist
            if (itm.barcodePkg) return 55
            return 35
          }}
        />
      </>
    }
    <Link to={`/good-issue-order/${orderType}/${id}`} target="_blank"
      className='float-right '>
      <h6 className='text-blue-600 underline '>กดดูใบเบิกหลัก PD Order {id}</h6>
    </Link>
    <Modal show={showConfirmDialog}
      onHide={closeDialog}
      okText="อนุมัติ"
      okTheme='success'
      okAction={onApproved}
      cancelText="ยกเลิก"
      title={`อนุมัติใบเบิก #${id}?`}
      loading={isSubmitting}
    >
      <div className='flex flex-col items-center gap-2 text-center'>
        <AiOutlineQuestionCircle size={40}/>
        <h5>อนุมัติใบเบิกเพิ่ม {id} คุณยืนยันใช่หรือไม่ ?</h5>
        <h6 className=' text-muted'>ระบบจะส่งต่อใบเบิกไปให้ PD เพื่อทำรับสินค้าต่อไป</h6>
      </div>
    </Modal>
    <Modal show={showDialogCancel} onHide={() => setShowCancel(false)}
      okTheme='danger'
      okText='ยืนยัน'
      cancelAction={() => setShowCancel(false)}
      okAction={onCancel}
      cancelText='ปิด'
      title={`คำเตือนเนื่องจากมีการยกเลิกใบเบิก`}
      >
      <div className='flex items-center gap-2'>
        <ImCancelCircle size={50} className='text-red-500 basis-1/6 ' />
        <div className='space-y-2 basis-5/6'>
          <h6>คุณกำลังจะยกเลิกใบเบิกเพิ่มของ PD ORDER</h6>
          <h5 className='font-bold underline '>{id}</h5>
          <h6 >({extraId})</h6>
          <h6>กลับไปที่ Production แน่ใจแล้วหรือไม่ ?</h6>
        </div>
      </div>
    </Modal>
  </div>
}

export default GoodIssueDetailExtra